import { StorageCheckoutData } from '@root/components/checkout/data';

type Args = {
  values: StorageCheckoutData;
  disposalEligible: boolean;
  /** Lead token from checkout values or client-data context. */
  leadToken?: string;
  storageEligible: boolean;
  loadingGeocode: boolean;
};

/** Street + state + ZIP from address suggestions — required before calendar fetch. */
export const hasChallengerPickupAddress = (
  address: StorageCheckoutData['address'],
): boolean =>
  !!(
    address?.street &&
    String(address.street).trim() &&
    address?.city &&
    String(address.city).trim() &&
    address?.state &&
    String(address.state).trim() &&
    address?.zip &&
    String(address.zip).trim()
  );

/**
 * Human-readable gaps that keep Reserve disabled on challenger page 1.
 * Contact (name / email / phone) lives on Confirm — not required here.
 */
const challengerStoragePlanMissingSteps = ({
  values,
  disposalEligible,
  storageEligible,
  loadingGeocode,
}: Args): string[] => {
  if (loadingGeocode) return ['Confirming your ZIP…'];
  if (!storageEligible) return ['Enter a storage-eligible ZIP code'];

  const {
    zipValidated,
    planSize,
    commitment,
    protectionPlan,
    bundleKind,
    disposalSelected,
    address,
    dateScheduled,
  } = values;

  const missing: string[] = [];
  if (!zipValidated) missing.push('Apply a valid ZIP under Storage location');
  if (planSize === undefined || commitment === undefined) {
    missing.push('Choose a storage plan and commitment');
  }
  if (protectionPlan === undefined) missing.push('Choose a protection plan');
  if (!bundleKind) {
    missing.push('Choose a packing option (I’ll pack myself or Full-service)');
  }
  if (disposalEligible && disposalSelected === undefined) {
    missing.push('Answer the disposal question');
  }
  if (!hasChallengerPickupAddress(address)) {
    missing.push(
      'Select a complete pickup address from the suggestions (street, city, state, ZIP)',
    );
  }
  if (!dateScheduled) {
    missing.push('Choose a pickup date and arrival window');
  }
  return missing;
};

/**
 * Challenger page-1 completion (Figma Desktop 03).
 * Contact (name / email / phone) lives on Confirm reservation — not required
 * to enable Reserve. Lead create runs when contact is already present, or later
 * on confirm.
 */
export const isChallengerStoragePlanComplete = (args: Args): boolean =>
  challengerStoragePlanMissingSteps(args).length === 0;

/** Kept for callers that still care about an existing lead (e.g. draft sync). */
export const hasLeadToken = ({
  values,
  leadToken,
}: Pick<Args, 'values' | 'leadToken'>): boolean =>
  !!(values.leadToken || leadToken);
